import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, CalendarDays } from 'lucide-react';

interface SingleDateRangePickerProps {
  startDate: string;
  endDate: string;
  onDateChange: (startDate: string, endDate: string) => void;
  placeholder?: string;
  buttonClassName?: string;
  position?: 'left' | 'right';
}

const SingleDateRangePicker: React.FC<SingleDateRangePickerProps> = ({
  startDate = '',
  endDate = '',
  onDateChange,
  placeholder = 'Select Date Range',
  buttonClassName = '',
  position = 'left'
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateSelectionMode, setDateSelectionMode] = useState<'start' | 'end'>('start');
  const [calendarViewMonth, setCalendarViewMonth] = useState(new Date().getMonth());
  const [calendarViewYear, setCalendarViewYear] = useState(new Date().getFullYear());
  const [tempDateFilters, setTempDateFilters] = useState({
    startDate: startDate || '',
    endDate: endDate || ''
  });
  const datePickerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Update temp dates when props change
  useEffect(() => {
    setTempDateFilters({
      startDate: startDate || '',
      endDate: endDate || ''
    });
  }, [startDate, endDate]);

  // Auto-scroll calendar into view when it opens
  useEffect(() => {
    if (showDatePicker && calendarRef.current) {
      setTimeout(() => {
        calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }, 100);
    }
  }, [showDatePicker]);

  // Helper to format date as YYYY-MM-DD
  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date for display (DD-MMM format)
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    if (year && month && day) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${day}-${monthNames[parseInt(month) - 1]}`;
    }
    return dateString;
  };

  // Get current month's first and last day as default
  const getCurrentMonthRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: formatDateString(firstDay),
      endDate: formatDateString(lastDay)
    };
  };

  // Check if date is in the future
  const isDateInFuture = (date: Date): boolean => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);
    return dateToCheck > today;
  };

  // Check if date is in selected range
  const isDateInRange = (date: Date, startDate: string, endDate: string): boolean => {
    if (!startDate || !endDate) return false;
    const dateStr = formatDateString(date);
    return dateStr >= startDate && dateStr <= endDate;
  };

  // Check if date is start date
  const isStartDate = (date: Date, startDate: string): boolean => {
    if (!startDate) return false;
    const dateStr = formatDateString(date);
    return dateStr === startDate;
  };

  // Check if date is end date
  const isEndDate = (date: Date, endDate: string): boolean => {
    if (!endDate) return false;
    const dateStr = formatDateString(date);
    return dateStr === endDate;
  };

  // Handler for date selection
  const handleDateSelect = (selectedDate: Date, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    if (isDateInFuture(selectedDate)) {
      return;
    }

    const dateStr = formatDateString(selectedDate);

    if (dateSelectionMode === 'start') {
      const currentEndDate = tempDateFilters.endDate || endDate;
      if (currentEndDate && dateStr > currentEndDate) {
        setTempDateFilters({ startDate: dateStr, endDate: dateStr });
        setDateSelectionMode('end');
      } else {
        setTempDateFilters(prev => ({ ...prev, startDate: dateStr }));
        setDateSelectionMode('end');
      }
    } else {
      const currentStartDate = tempDateFilters.startDate || startDate;
      if (dateStr < currentStartDate) {
        setTempDateFilters({ startDate: dateStr, endDate: currentStartDate });
      } else {
        setTempDateFilters(prev => ({ ...prev, endDate: dateStr }));
      }
    }
  };

  // Apply date filter
  const handleApplyDateFilter = () => {
    if (onDateChange) {
      onDateChange(tempDateFilters.startDate, tempDateFilters.endDate);
    }
    setShowDatePicker(false);
  };

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  const now = new Date();
  const currentMonthDate = new Date(calendarViewYear, calendarViewMonth, 1);

  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date): number => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const handlePrevMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (calendarViewMonth === 0) {
      setCalendarViewMonth(11);
      setCalendarViewYear(calendarViewYear - 1);
    } else {
      setCalendarViewMonth(calendarViewMonth - 1);
    }
  };

  const handleNextMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    if (calendarViewYear === currentYear && calendarViewMonth === currentMonth) {
      return;
    }

    if (calendarViewMonth === 11) {
      const nextYear = calendarViewYear + 1;
      if (nextYear <= currentYear) {
        setCalendarViewMonth(0);
        setCalendarViewYear(nextYear);
      }
    } else {
      const nextMonth = calendarViewMonth + 1;
      if (calendarViewYear < currentYear || (calendarViewYear === currentYear && nextMonth <= currentMonth)) {
        setCalendarViewMonth(nextMonth);
      }
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    const newYear = parseInt(e.target.value);
    const currentYear = now.getFullYear();
    if (newYear <= currentYear) {
      setCalendarViewYear(newYear);
      if (newYear === currentYear && calendarViewMonth > now.getMonth()) {
        setCalendarViewMonth(now.getMonth());
      }
    }
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    const newMonth = parseInt(e.target.value);
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    if (calendarViewYear < currentYear || (calendarViewYear === currentYear && newMonth <= currentMonth)) {
      setCalendarViewMonth(newMonth);
    }
  };

  const renderCalendarMonth = (monthDate: Date) => {
    const daysInMonth = getDaysInMonth(monthDate);
    const firstDay = getFirstDayOfMonth(monthDate);
    const today = new Date();
    const isTodayMonth = monthDate.getMonth() === today.getMonth() && monthDate.getFullYear() === today.getFullYear();

    const days: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return (
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={handlePrevMonth}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            type="button"
          >
            <ChevronDown className="w-4 h-4 rotate-90 text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <select
              value={monthDate.getMonth()}
              onChange={handleMonthChange}
              className="text-sm font-medium border-none bg-transparent cursor-pointer focus:outline-none"
              onClick={(e) => e.stopPropagation()}
            >
              {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, idx) => {
                const currentYear = now.getFullYear();
                const currentMonth = now.getMonth();
                const isFutureMonth = calendarViewYear === currentYear && idx > currentMonth;
                return (
                  <option key={idx} value={idx} disabled={isFutureMonth}>{month}</option>
                );
              })}
            </select>
            <select
              value={monthDate.getFullYear()}
              onChange={handleYearChange}
              className="text-sm font-medium border-none bg-transparent cursor-pointer focus:outline-none"
              onClick={(e) => e.stopPropagation()}
            >
              {Array.from({ length: 10 }, (_, i) => {
                const year = now.getFullYear() - 9 + i;
                if (year <= now.getFullYear()) {
                  return <option key={year} value={year}>{year}</option>;
                }
                return null;
              })}
            </select>
          </div>
          <button
            onClick={handleNextMonth}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            type="button"
          >
            <ChevronDown className="w-4 h-4 -rotate-90 text-gray-600" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
            <div key={day} className="w-8 h-8 flex items-center justify-center text-xs text-gray-500 font-medium">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 text-sm">
          {days.map((day, idx) => {
            if (day === null) {
              return <div key={idx} className="w-8 h-8"></div>;
            }

            const cellDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
            const isToday = isTodayMonth && day === today.getDate();
            const dateInRange = tempDateFilters.startDate && tempDateFilters.endDate && isDateInRange(cellDate, tempDateFilters.startDate, tempDateFilters.endDate);
            const isStart = isStartDate(cellDate, tempDateFilters.startDate);
            const isEnd = isEndDate(cellDate, tempDateFilters.endDate);
            const dateSelected = isStart || isEnd;
            const isFuture = isDateInFuture(cellDate);

            return (
              <div
                key={idx}
                onClick={(e) => !isFuture && handleDateSelect(cellDate, e)}
                className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
                  isFuture ? "text-gray-300 cursor-not-allowed" :
                  dateSelected ? "bg-jd-green text-white font-semibold cursor-pointer" :
                  dateInRange ? "bg-green-100 text-green-800 cursor-pointer" :
                  isToday ? "bg-blue-100 text-blue-600 font-semibold cursor-pointer" :
                  "text-gray-700 hover:bg-gray-100 cursor-pointer"
                }`}
              >
                {day}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const displayText = startDate && endDate
    ? `${formatDateForDisplay(startDate)} → ${formatDateForDisplay(endDate)}`
    : placeholder;

  return (
    <div className="relative" ref={datePickerRef}>
      <button
        type="button"
        onClick={() => {
          setShowDatePicker(!showDatePicker);
          setDateSelectionMode('start');
          setTempDateFilters({
            startDate: startDate || '',
            endDate: endDate || ''
          });
        }}
        className={`flex items-center justify-between gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm hover:bg-gray-50 transition-colors ${buttonClassName}`}
      >
        <div className="flex items-center gap-2 flex-1">
          <CalendarDays className="h-4 w-4 text-gray-500 flex-shrink-0" />
          <span className={`text-sm font-medium ${startDate && endDate ? 'text-gray-900' : 'text-gray-400'}`}>
            {displayText}
          </span>
        </div>
        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </button>

      {showDatePicker && (
        <div 
          ref={calendarRef}
          className={`absolute top-full ${position === 'right' ? 'right-0' : 'left-0'} mt-2 z-[100] bg-white rounded-lg shadow-md border border-gray-200 p-3 sm:p-4 w-auto min-w-[280px] sm:min-w-[300px] max-w-[calc(100vw-2rem)]`} 
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-3 text-xs text-gray-500 text-center">
            {dateSelectionMode === 'start' ? 'Select Start Date' : 'Select End Date'}
          </div>
          <div className="flex justify-center">
            {renderCalendarMonth(currentMonthDate)}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const currentMonthRange = getCurrentMonthRange();
                setTempDateFilters({ startDate: currentMonthRange.startDate, endDate: currentMonthRange.endDate });
                setDateSelectionMode('start');
                setCalendarViewMonth(now.getMonth());
                setCalendarViewYear(now.getFullYear());
              }}
              className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              type="button"
            >
              Reset to Current Month
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleApplyDateFilter();
              }}
              className="px-3 py-1.5 text-xs bg-jd-green hover:bg-green-700 text-white rounded-md transition-colors"
              type="button"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleDateRangePicker;
